// API configuration
// Always use Cloudflare Worker API backend
// Same-origin API requests route through the local dev proxy or Cloudflare's /api/* route.
const API_BASE_URL = '';

// Helper function for API requests
async function apiRequest(endpoint, options = {}) {
  const cacheBuster = `cb=${Date.now()}`;
  const sep = endpoint.includes('?') ? '&' : '?';
  const url = `${API_BASE_URL}${endpoint}${sep}${cacheBuster}`;
  
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, { ...config, cache: 'no-store' });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
}

// Helper function to handle API responses
async function handleResponse(response) {
  const data = await response.json();
  
  if (!response.ok) {
    const error = new Error(data.message || data.error || 'API request failed');
    error.code = data.code;
    error.details = data;
    error.status = response.status;
    throw error;
  }
  
  return data;
}

// Helper: generate uuid v4 using Web Crypto
function generateUuidV4() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Polyfill using getRandomValues
  const bytes = new Uint8Array(16);
  (window.crypto || window.msCrypto).getRandomValues(bytes);
  // Per RFC4122 v4
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0'));
  return `${hex[0]}${hex[1]}${hex[2]}${hex[3]}-${hex[4]}${hex[5]}-${hex[6]}${hex[7]}-${hex[8]}${hex[9]}-${hex[10]}${hex[11]}${hex[12]}${hex[13]}${hex[14]}${hex[15]}`;
}

// Helper function to get the original anonymous user ID (for file linking)
export function getOriginalUserId() {
  return localStorage.getItem('fileshare_original_user_id');
}

// Helper function to get user ID with cross-tab stability and unified format: user_<uuidv4>
export function getUserId() {
  const KEY = 'fileshare_user_id';
  const ORIGINAL_KEY = 'fileshare_original_user_id'; // Store original anonymous ID
  const COOKIE_NAME = 'fileshare_user_id';
  const STANDARD_RE = /^user_[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  try {
    // Prefer the account identity once the backend has linked this browser's
    // anonymous uploads to the authenticated account.
    const authenticatedUserId = window.__authContext?.isAuthenticated
      ? window.__authContext.userId
      : null;
    if (authenticatedUserId) return authenticatedUserId;

    // UNIFIED SYSTEM: ALWAYS use the original UUID, regardless of authentication status
    // Firebase/email is ONLY for authentication, NOT for user identity
    
    // 1) PRIORITIZE COOKIE (persists across browser sessions)
    let existing = null;
    const m = document.cookie.match(/(?:^|; )fileshare_user_id=([^;]+)/);
    if (m) {
      existing = decodeURIComponent(m[1]);
      console.log('🍪 Found user ID in cookie:', existing);
    }
    
    // 2) Fallback to localStorage (current session only)
    if (!existing) {
      existing = localStorage.getItem(KEY);
      if (existing) {
        console.log('💾 Found user ID in localStorage:', existing);
      }
    }

    // 2) Normalize to standard UUID format if present
    if (existing) {
      // If it's already in standard UUID format, use it
      if (STANDARD_RE.test(existing)) {
        return existing;
      }
      
      // If it's an old Firebase UID format (user_{firebaseUid}), migrate back to UUID
      // This handles cases where users were authenticated before the fix
      if (existing.startsWith('user_') && !STANDARD_RE.test(existing)) {
        // Check if we have the original anonymous UUID saved
        const originalId = localStorage.getItem(ORIGINAL_KEY);
        if (originalId && STANDARD_RE.test(originalId)) {
          console.log(`🔄 Migrating back to original UUID: ${originalId} (was: ${existing})`);
          const exp = new Date();
          exp.setFullYear(exp.getFullYear() + 1);
          const domain = window.location.hostname === 'localhost' ? '' : `; domain=.largetransfer.net`;
          document.cookie = `${COOKIE_NAME}=${encodeURIComponent(originalId)}; expires=${exp.toUTCString()}; path=/${domain}; SameSite=Lax`;
          localStorage.setItem(KEY, originalId);
          return originalId;
        }
        
        // No original UUID found, create a new one
        console.log(`⚠️ No original UUID found, creating new one (was: ${existing})`);
        const newId = `user_${generateUuidV4()}`;
        const exp = new Date();
        exp.setFullYear(exp.getFullYear() + 1);
        const domain = window.location.hostname === 'localhost' ? '' : `; domain=.largetransfer.net`;
        document.cookie = `${COOKIE_NAME}=${encodeURIComponent(newId)}; expires=${exp.toUTCString()}; path=/${domain}; SameSite=Lax`;
        localStorage.setItem(KEY, newId);
        localStorage.setItem(ORIGINAL_KEY, newId);
        return newId;
      }
      
      // Other old formats, create new UUID
      const newId = `user_${generateUuidV4()}`;
      const exp = new Date();
      exp.setFullYear(exp.getFullYear() + 1);
      const domain = window.location.hostname === 'localhost' ? '' : `; domain=.largetransfer.net`;
      document.cookie = `${COOKIE_NAME}=${encodeURIComponent(newId)}; expires=${exp.toUTCString()}; path=/${domain}; SameSite=Lax`;
      localStorage.setItem(KEY, newId);
      localStorage.setItem(ORIGINAL_KEY, newId);
      return newId;
    }

    // 3) Create brand-new standard UUID for new users
    const newId = `user_${generateUuidV4()}`;
    const exp = new Date();
    exp.setFullYear(exp.getFullYear() + 1);

    try {
      // Store in BOTH cookie (persistent) and localStorage (session)
      const domain = window.location.hostname === 'localhost' ? '' : `; domain=.largetransfer.net`;
      document.cookie = `${COOKIE_NAME}=${encodeURIComponent(newId)}; expires=${exp.toUTCString()}; path=/${domain}; SameSite=Lax`;
      localStorage.setItem(KEY, newId);
      localStorage.setItem(ORIGINAL_KEY, newId); // Store as original
      console.log(`🆕 Created new user ID: ${newId} (stored in cookie + localStorage)`);
    } catch (storageError) {
      console.error('Failed to store user ID:', storageError);
    }

    // Cross-tab race mitigation: adopt the first writer on next tick
    setTimeout(() => {
      const winner = localStorage.getItem(KEY);
      if (winner && winner !== newId) {
        const exp2 = new Date();
        exp2.setFullYear(exp2.getFullYear() + 1);
        const domain = window.location.hostname === 'localhost' ? '' : `; domain=.largetransfer.net`;
        document.cookie = `${COOKIE_NAME}=${encodeURIComponent(winner)}; expires=${exp2.toUTCString()}; path=/${domain}; SameSite=Lax`;
      }
    }, 0);

    return newId;
  } catch (e) {
    // Fallback: deterministic lightweight ID, prefixed for format consistency
    let userId = localStorage.getItem(KEY);
    if (!userId) {
      const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
      let rnd = '';
      for (let i = 0; i < 16; i++) rnd += chars.charAt(Math.floor(Math.random() * chars.length));
      userId = `user_${rnd}`;
      localStorage.setItem(KEY, userId);
    }
    return userId;
  }
}

export async function getUserIdWhenReady() {
  if (typeof window === 'undefined') return null;

  const ensureAuthReady = window.__ensureAuthReady;
  if (ensureAuthReady) await ensureAuthReady();
  const registrationPromise = window.__authContext?.registrationPromise;
  if (registrationPromise) await registrationPromise;
  return getUserId();
}

// FORCE USER ID CONSISTENCY - Override backend response
function ensureUserIdConsistency(data, providedUserId) {
  if (providedUserId && data.userId && data.userId !== providedUserId) {
    console.warn('Backend returned different user ID, maintaining consistency');
    console.log('Provided:', providedUserId, 'Backend returned:', data.userId);
    
    // Keep the consistent user ID in localStorage
    localStorage.setItem('fileshare_user_id', providedUserId);
    
    // Override the response to maintain consistency
    data.userId = providedUserId;
  }
  return data;
}

// Upload API
export const uploadAPI = {
  /**
   * Initialize upload and get signed URLs
   */
  async initializeUpload(files, options = {}) {
    const userId = options.userId || await getUserIdWhenReady();
    
    console.log(`Initializing upload for ${files.length} files to: ${API_BASE_URL}`);
    console.log('Upload options:', options);
    
    const requestBody = {
      files: files.map(file => ({
        name: file.name,
        size: file.size,
        type: file.type,
        // Per-file idempotencyKey to ensure retries don't create duplicates
        idempotencyKey: `${userId}:${options.folderId || 'new'}:${file.name}:${file.size}`
      })),
      userId: userId !== 'temp_' + userId.slice(5) ? userId : null,
      expiryDays: (options.expiryDays !== undefined && options.expiryDays !== null) ? options.expiryDays : 1
    };

    // Add folder information if uploading to existing folder
    if (options.folderId) {
      requestBody.folderId = options.folderId;
      requestBody.userId = options.userId || userId;
      console.log(`Uploading to existing folder: ${options.folderId}`);
    }
    
    const response = await fetch(`${API_BASE_URL}/api/upload/init-batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });

    console.log(`Backend response status: ${response.status}`);
    
    // Handle storage and bandwidth exceeded errors
    if (response.status === 403) {
      try {
        const cloned = response.clone();
        const json = await cloned.json();
        if (json?.code === 'BANDWIDTH_EXCEEDED') {
          window.__bandwidthBlocked = { active: true, details: json };
          window.dispatchEvent(new CustomEvent('bandwidth-exceeded', { detail: json }));
        } else if (json?.code === 'STORAGE_EXCEEDED') {
          window.__storageBlocked = { active: true, details: json };
          window.dispatchEvent(new CustomEvent('storage-quota-exceeded', { detail: json }));
        }
      } catch (_) {}
    }
    
    const data = await handleResponse(response);
    console.log('Upload initialized successfully:', data);
    
    // Ensure user ID consistency
    const finalData = ensureUserIdConsistency(data, options.userId || userId);
    
    // Only update user ID if we didn't provide one (for backward compatibility)
    if (finalData.userId && !options.userId) {
      localStorage.setItem('fileshare_user_id', finalData.userId);
      console.log('Updated localStorage with new user ID:', finalData.userId);
    }
    
    return finalData;
  },

  /**
   * Upload file directly to R2 using signed URL (OPTIMIZED VERSION)
   */
  async uploadToR2(file, uploadUrl, onProgress) {
    const startTime = Date.now();
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      // Optimized timeout for large files (based on file size)
      const timeoutMinutes = Math.max(5, Math.ceil(file.size / (50 * 1024 * 1024))); // 5 min base + 1 min per 50MB
      xhr.timeout = timeoutMinutes * 60 * 1000;
      
      // Enable high-performance uploads
      xhr.withCredentials = false; // Disable credentials for faster uploads
      
      let lastReportedPercent = 0;
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const percentComplete = (e.loaded / e.total) * 100;
          lastReportedPercent = Math.max(lastReportedPercent, Math.min(100, percentComplete));
          onProgress(lastReportedPercent);
        }
      });
      
      xhr.addEventListener('load', () => {
        console.log(`R2 Direct Upload completed - Status: ${xhr.status}`);
        if (xhr.status >= 200 && xhr.status < 300) {
          try { uploadMetrics.record(Date.now() - startTime, { method: 'single', size: file.size }); } catch {}
          resolve(xhr.response);
        } else {
          const errorMsg = `Upload failed with status ${xhr.status}: ${xhr.statusText}`;
          console.error(errorMsg);
          reject(new Error(errorMsg));
        }
      });
      
      xhr.addEventListener('error', (e) => {
        const errorMsg = `Network error during upload: ${e.type}`;
        console.error(errorMsg, e);
        reject(new Error(errorMsg));
      });
      
      xhr.addEventListener('timeout', () => {
        const errorMsg = `Upload timed out after ${timeoutMinutes} minutes`;
        console.error(errorMsg);
        reject(new Error(errorMsg));
      });
      
      xhr.addEventListener('abort', () => {
        const errorMsg = 'Upload was aborted';
        console.error(errorMsg);
        reject(new Error(errorMsg));
      });
      
      try {
        console.log(`Starting OPTIMIZED R2 upload for file: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
        console.log(`Upload URL: ${typeof uploadUrl === 'string' ? uploadUrl.substring(0, 100) + '...' : uploadUrl}`);
        
        xhr.open('PUT', uploadUrl);
        
        // Optimized headers for R2
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        
        // Start upload
        xhr.send(file);
        
      } catch (error) {
        console.error('Error starting R2 upload:', error);
        reject(new Error(`Failed to start upload: ${error.message}`));
      }
    });
  },

  /**
   * HIGH-PERFORMANCE MULTIPART UPLOAD - Maximum speed and reliability
   * Features: Adaptive chunk sizing, parallel uploads, checksum validation, resumable uploads
   */
  async uploadToR2Multipart(file, fileId, r2Key, onProgress) {
    const uploadStart = Date.now();
    const MAX_PARALLEL_UPLOADS = 3; // 3 parallel uploads for stability
    const MAX_RETRIES = 3; // 3 automatic retries with exponential backoff
    
    let uploadId = null;
    let uploadSession = null;

    // Local resume helpers
    const sessionKey = (fid) => `lt.multipart.session:${fid}`;
    const loadLocalSession = (fid) => {
      try { const raw = localStorage.getItem(sessionKey(fid)); return raw ? JSON.parse(raw) : null; } catch { return null; }
    };
    const saveLocalSession = (fid, data) => { try { localStorage.setItem(sessionKey(fid), JSON.stringify(data)); } catch {} };
    const clearLocalSession = (fid) => { try { localStorage.removeItem(sessionKey(fid)); } catch {} };
    
    try {
      console.log(`🚀 Starting HIGH-PERFORMANCE multipart upload: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
      
      // Step 1: Check for existing upload session (resumable uploads)
      console.log('🔍 Checking for existing upload session...');
      const resumeResponse = await fetch(`${API_BASE_URL}/api/upload/multipart/resume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileId, r2Key })
      });
      
      if (resumeResponse.ok) {
        uploadSession = await resumeResponse.json();
        uploadId = uploadSession.uploadId;
        console.log(`✅ Resuming existing upload session: ${uploadId}`);
        // Persist session
        saveLocalSession(fileId, { uploadId, r2Key, completedParts: uploadSession?.completedParts || [] });
      } else {
        // Step 2: Create new multipart upload session
        console.log('📝 Creating new multipart upload session...');
        const createResponse = await fetch(`${API_BASE_URL}/api/upload/multipart/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileId,
            r2Key,
            contentType: file.type || 'application/octet-stream'
          })
        });
        
        if (!createResponse.ok) {
          throw new Error(`Failed to create multipart upload: ${createResponse.statusText}`);
        }
        
        uploadSession = await createResponse.json();
        uploadId = uploadSession.uploadId;
        console.log(`✅ New multipart upload session created: ${uploadId}`);
        // Persist session
        saveLocalSession(fileId, { uploadId, r2Key, completedParts: [] });
      }
      
      // Step 3: Dynamic chunk sizing to guarantee total chunks stay well under R2/S3 10,000 part limit
      // Supports files from 100MB up to 1TB without exceeding part limits
      const minRequiredChunkFor10000Parts = Math.ceil(file.size / 3500);
      const MIN_CHUNK_SIZE = 25 * 1024 * 1024; // 25MB minimum chunk size
      const CHUNK_SIZE = Math.max(MIN_CHUNK_SIZE, minRequiredChunkFor10000Parts);
      
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      console.log(`📦 Using ${totalChunks} chunks of ${(CHUNK_SIZE / 1024 / 1024).toFixed(1)}MB each (well within 10,000 part limit)`);
      
      // Step 4: Get completed parts for resumable uploads (prefer local cache if exists)
      let completedParts = [];
      const localCompleted = loadLocalSession(fileId)?.completedParts;
      if (Array.isArray(localCompleted) && localCompleted.length > 0) {
        completedParts = localCompleted;
      } else if (uploadSession?.completedParts) {
        completedParts = uploadSession.completedParts;
      }
      if (completedParts.length > 0) {
        console.log(`🔄 Resuming upload: ${completedParts.length}/${totalChunks} parts already completed`);
      }
      
      // Step 5: Define chunk boundaries WITHOUT heavy upfront slicing or blocking CPU hashing
      const chunks = [];
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const partNumber = i + 1;
        chunks.push({
          partNumber,
          start,
          end,
          size: end - start,
          checksum: `${fileId}_part_${partNumber}`,
          isCompleted: completedParts.some(part => part.partNumber === partNumber)
        });
      }
      
      // Step 6: Upload chunks with parallel processing and adaptive sizing
      let uploadedBytes = completedParts.reduce((sum, part) => sum + part.size, 0);
      const pendingChunks = chunks.filter(chunk => !chunk.isCompleted);
      
      // Thread-safe in-flight chunk progress tracker to prevent progress jumping between parallel chunks
      const inFlightBytesByPart = new Map();
      let lastReportedPercent = file.size > 0 ? (uploadedBytes / file.size) * 100 : 0;

      const reportAggregatedProgress = () => {
        if (!onProgress) return;
        let inFlightTotal = 0;
        for (const loaded of inFlightBytesByPart.values()) {
          inFlightTotal += loaded;
        }
        const totalUploaded = Math.min(file.size, uploadedBytes + inFlightTotal);
        const percent = file.size > 0 ? (totalUploaded / file.size) * 100 : 0;
        // Strictly monotonic: progress can never go backwards
        lastReportedPercent = Math.max(lastReportedPercent, Math.min(percent, 100));
        onProgress(lastReportedPercent);
      };

      console.log(`🚀 Uploading ${pendingChunks.length} pending chunks with ${MAX_PARALLEL_UPLOADS} parallel uploads...`);
      
      const uploadChunk = async (chunk, retryCount = 0) => {
        return new Promise(async (resolve, reject) => {
          try {
            // Get signed URL for this chunk
            const urlResponse = await fetch(`${API_BASE_URL}/api/upload/multipart/part-url`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                uploadId,
                partNumber: chunk.partNumber,
                r2Key,
                checksum: chunk.checksum
              })
            });
            
            if (!urlResponse.ok) {
              throw new Error(`Failed to get upload URL for part ${chunk.partNumber}`);
            }
            
            const { uploadUrl } = await urlResponse.json();
            
            // Calculate timeout proportional to chunk size (60s per 1MB for better reliability)
            const timeoutMs = Math.max(60000, (chunk.size / (1024 * 1024)) * 60000);
            
            console.log(`⬆️ Uploading chunk ${chunk.partNumber}/${totalChunks} (${(chunk.size / 1024 / 1024).toFixed(2)}MB)${retryCount > 0 ? ` - Retry ${retryCount}` : ''}`);
            console.log(`🔗 Upload URL: ${uploadUrl.substring(0, 100)}...`);
            console.log(`⏱️ Timeout: ${timeoutMs}ms`);
            
            const xhr = new XMLHttpRequest();
            xhr.timeout = timeoutMs;
            
            xhr.upload.addEventListener('progress', (e) => {
              if (e.lengthComputable) {
                inFlightBytesByPart.set(chunk.partNumber, Math.min(chunk.size, e.loaded));
                reportAggregatedProgress();
              }
            });
            
            xhr.addEventListener('load', async () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                const etag = xhr.getResponseHeader('ETag') || xhr.getResponseHeader('etag');
                if (!etag) {
                  inFlightBytesByPart.delete(chunk.partNumber);
                  reject(new Error(`No ETag received for part ${chunk.partNumber}`));
                  return;
                }
                
                console.log(`✅ Chunk ${chunk.partNumber} uploaded successfully`);
                
                // Record completed part with checksum validation
                try {
                  await fetch(`${API_BASE_URL}/api/upload/multipart/part-complete`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      uploadId,
                      partNumber: chunk.partNumber,
                      etag: etag.replace(/"/g, ''),
                      size: chunk.size,
                      checksum: chunk.checksum
                    })
                  });
                } catch (error) {
                  console.warn(`Failed to record part completion: ${error.message}`);
                }
                
                // Update state: transfer in-flight bytes to completed uploadedBytes
                inFlightBytesByPart.delete(chunk.partNumber);
                uploadedBytes += chunk.size;
                reportAggregatedProgress();

                completedParts.push({ partNumber: chunk.partNumber, size: chunk.size, checksum: chunk.checksum });
                saveLocalSession(fileId, { uploadId, r2Key, completedParts });
                
                resolve({
                  partNumber: chunk.partNumber,
                  etag: etag.replace(/"/g, ''),
                  size: chunk.size,
                  checksum: chunk.checksum
                });
              } else {
                inFlightBytesByPart.delete(chunk.partNumber);
                reject(new Error(`Part ${chunk.partNumber} upload failed: ${xhr.status} ${xhr.statusText}`));
              }
            });
            
            xhr.addEventListener('error', (e) => {
              inFlightBytesByPart.delete(chunk.partNumber);
              console.error(`❌ Network error uploading part ${chunk.partNumber}:`, e);
              reject(new Error(`Network error uploading part ${chunk.partNumber}`));
            });
            
            xhr.addEventListener('timeout', () => {
              inFlightBytesByPart.delete(chunk.partNumber);
              reject(new Error(`Timeout uploading part ${chunk.partNumber} (${timeoutMs}ms)`));
            });
            
            // Upload chunk directly to R2 using signed URL (sliced on-demand to keep memory footprint minimal)
            const chunkBlob = chunk.blob || file.slice(chunk.start, chunk.end);
            xhr.open('PUT', uploadUrl);
            xhr.setRequestHeader('Content-Type', 'application/octet-stream');
            xhr.send(chunkBlob);
          } catch (error) {
            inFlightBytesByPart.delete(chunk.partNumber);
            console.error(`Error uploading chunk ${chunk.partNumber}:`, error);
            if (retryCount < MAX_RETRIES) {
              try { uploadMetrics.recordRetry(); } catch {}
              const retryDelayMs = Math.min(10000, 1000 * Math.pow(2, retryCount));
              console.log(`🔁 Retrying chunk ${chunk.partNumber} (attempt ${retryCount + 1}) in ${retryDelayMs}ms...`);
              setTimeout(() => {
                resolve(uploadChunk(chunk, retryCount + 1));
              }, retryDelayMs);
              return;
            }
            reject(error);
          }
        });
      };
      
      // Upload pending chunks in parallel batches
      const newCompletedParts = [...completedParts];
      
      for (let i = 0; i < pendingChunks.length; i += MAX_PARALLEL_UPLOADS) {
        const batch = pendingChunks.slice(i, i + MAX_PARALLEL_UPLOADS);
        const batchResults = await Promise.all(batch.map(uploadChunk));
        newCompletedParts.push(...batchResults);
        // Persist incremental progress
        saveLocalSession(fileId, { uploadId, r2Key, completedParts: newCompletedParts });
      }
      
      // Sort parts by part number
      newCompletedParts.sort((a, b) => a.partNumber - b.partNumber);
      
      console.log(`✅ All ${newCompletedParts.length} chunks uploaded successfully`);
      
      // Step 7: Complete multipart upload
      console.log('🔗 Completing multipart upload...');
      const completeResponse = await fetch(`${API_BASE_URL}/api/upload/multipart/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uploadId,
          r2Key,
          parts: newCompletedParts
        })
      });
      
      if (!completeResponse.ok) {
        throw new Error(`Failed to complete multipart upload: ${completeResponse.statusText}`);
      }
      
      const result = await completeResponse.json();
      console.log(`🎉 High-performance multipart upload completed successfully!`);
      // Clear local session
      clearLocalSession(fileId);
      try { uploadMetrics.record(Date.now() - uploadStart, { method: 'multipart', size: file.size }); } catch {}
      return result;
    } catch (error) {
      console.error('❌ High-performance multipart upload failed:', error);
      // Try to abort the multipart upload on error
      try {
        if (uploadId) {
          console.log('🚫 Attempting to abort failed multipart upload...');
          await fetch(`${API_BASE_URL}/api/upload/multipart/abort`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uploadId, r2Key })
          });
          console.log('✅ Multipart upload aborted successfully');
        }
      } catch (abortError) {
        console.error('Failed to abort multipart upload:', abortError);
      }
      throw error;
    }
  },

  /**
   * Calculate checksum for a blob using Web Crypto API
   */
  async calculateChecksum(blob) {
    try {
      const buffer = await blob.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      return hashHex;
    } catch (error) {
      console.warn('Failed to calculate checksum, using fallback:', error);
      // Fallback: simple hash based on size and first/last bytes
      const size = blob.size;
      const firstBytes = await blob.slice(0, 8).arrayBuffer();
      const lastBytes = await blob.slice(-8).arrayBuffer();
      const first = Array.from(new Uint8Array(firstBytes)).join('');
      const last = Array.from(new Uint8Array(lastBytes)).join('');
      return `${size}-${first}-${last}`;
    }
  },

  /**
   * CHUNKED UPLOAD - Simpler approach for large files
   * Upload large files by sending chunks to backend, backend assembles them
   */
  async uploadToR2Chunked(file, fileId, r2Key, onProgress) {
    const start = Date.now();
    const CHUNK_SIZE = 20 * 1024 * 1024; // 20MB chunks (well under 100MB limit)
    
    try {
      console.log(`🚀 Starting CHUNKED upload for large file: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
      
      // Step 1: Initialize chunked upload
      console.log('📝 Initializing chunked upload...');
      const initResponse = await fetch(`${API_BASE_URL}/api/upload/chunked/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileId,
          r2Key,
          fileName: file.name,
          fileSize: file.size,
          contentType: file.type || 'application/octet-stream'
        })
      });
      
      if (!initResponse.ok) {
        throw new Error(`Failed to initialize chunked upload: ${initResponse.statusText}`);
      }
      
      const { uploadId } = await initResponse.json();
      console.log(`✅ Chunked upload initialized with ID: ${uploadId}`);
      
      // Step 2: Split file into chunks
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      console.log(`📦 Splitting file into ${totalChunks} chunks of ${(CHUNK_SIZE / 1024 / 1024).toFixed(1)}MB each`);
      
      let uploadedBytes = 0;
      
      // Step 3: Upload chunks sequentially
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const chunkNumber = i + 1;
        
        console.log(`⬆️ Uploading chunk ${chunkNumber}/${totalChunks} (${(chunk.size / 1024 / 1024).toFixed(2)}MB)`);
        
        // Upload chunk to backend
        const formData = new FormData();
        formData.append('uploadId', uploadId);
        formData.append('chunkNumber', chunkNumber);
        formData.append('totalChunks', totalChunks);
        formData.append('chunk', chunk);
        
        const chunkResponse = await fetch(`${API_BASE_URL}/api/upload/chunked/chunk`, {
          method: 'POST',
          body: formData
        });
        
        if (!chunkResponse.ok) {
          throw new Error(`Failed to upload chunk ${chunkNumber}: ${chunkResponse.statusText}`);
        }
        
        uploadedBytes += chunk.size;
        const progress = (uploadedBytes / file.size) * 100;
        
        if (onProgress) {
          onProgress(Math.min(progress, 100));
        }
        
        console.log(`✅ Chunk ${chunkNumber} uploaded successfully (${progress.toFixed(1)}%)`);
        
        // Small delay between chunks
        if (i < totalChunks - 1) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
      
      // Step 4: Complete chunked upload
      console.log('🔗 Completing chunked upload...');
      const completeResponse = await fetch(`${API_BASE_URL}/api/upload/chunked/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uploadId })
      });
      
      if (!completeResponse.ok) {
        throw new Error(`Failed to complete chunked upload: ${completeResponse.statusText}`);
      }
      
      const result = await completeResponse.json();
      console.log(`🎉 Chunked upload completed successfully!`);
      try { uploadMetrics.record(Date.now() - start, { method: 'chunked', size: file.size }); } catch {}
      return result;
      
    } catch (error) {
      console.error('❌ Chunked upload failed:', error);
      throw error;
    }
  },

  /**
   * Smart upload method that automatically chooses between regular and multipart upload
   */
  async uploadToR2Smart(file, uploadUrl, fileId, r2Key, onProgress) {
    const MULTIPART_THRESHOLD = 100 * 1024 * 1024; // 100MB threshold
    
    if (file.size > MULTIPART_THRESHOLD) {
      console.log(`🔄 File size ${(file.size / 1024 / 1024).toFixed(2)}MB > 100MB, using multipart upload`);
      return this.uploadToR2Multipart(file, fileId, r2Key, onProgress);
    } else {
      console.log(`📤 File size ${(file.size / 1024 / 1024).toFixed(2)}MB <= 100MB, using standard upload`);
      return this.uploadToR2(file, uploadUrl, onProgress);
    }
  },

  /**
   * Mark upload as complete (sends metadata so backend can persist records)
   */
  async completeUpload(fileId, success = true, meta = {}) {
    const result = await this.completeUploads([{ fileId, success, meta }]);
    return result.results?.[0] || result;
  },

  /** Persist metadata for a batch before upload is reported as complete. */
  async completeUploads(completions = []) {
    if (!Array.isArray(completions) || completions.length === 0) {
      return { success: true, count: 0, results: [] };
    }

    const resolvedUserId = await getUserIdWhenReady();
    const files = completions.map(({ fileId, success = true, meta = {} }) => ({
      fileId,
      success,
      folderId: meta.folderId,
      userId: meta.userId || resolvedUserId,
      r2Key: meta.r2Key,
      filename: meta.filename,
      fileSize: meta.fileSize,
      contentType: meta.contentType,
      expiryDate: meta.expiryDate
    }));

    const results = [];
    const bookmarkUserId = files[0]?.userId || resolvedUserId;
    const bookmarkKey = bookmarkUserId ? `fileshare_d1_bookmark_${bookmarkUserId}` : null;
    for (let index = 0; index < files.length; index += 100) {
      const batch = files.slice(index, index + 100);
      let confirmedBatch = null;
      let lastError = null;
      for (let attempt = 0; attempt < 3 && !confirmedBatch; attempt++) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/upload/confirm-batch`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ files: batch })
          });
          const d1Bookmark = response.headers.get('X-D1-Bookmark');
          if (d1Bookmark && bookmarkKey) {
            try { localStorage.setItem(bookmarkKey, d1Bookmark); } catch (_) {}
          }
          const data = await handleResponse(response);
          const failed = (data.results || []).filter(item => !item.success);
          if (data.success && failed.length === 0 && !data.failed) {
            confirmedBatch = data;
          } else {
            lastError = new Error(`Could not save metadata for ${failed.length || data.failed || 1} uploaded file(s).`);
            lastError.details = data;
          }
        } catch (error) {
          lastError = error;
        }

        if (!confirmedBatch && attempt < 2) {
          await new Promise(resolve => setTimeout(resolve, 400 * (attempt + 1)));
        }
      }
      if (!confirmedBatch) {
        const error = new Error(`${lastError?.message || 'Could not confirm uploaded files.'} Please retry the upload confirmation.`);
        error.details = lastError?.details;
        throw error;
      }
      results.push(...(confirmedBatch.results || []));
    }

    return { success: true, count: results.length, results };
  },

  /**
   * Confirm completion only after the backend has saved the listing metadata.
   */
  async completeUploadAsync(fileId, success = true, meta = {}) {
    return this.completeUpload(fileId, success, meta);
  },

  /**
   * ENTERPRISE: Verify that upload completed successfully
   * This ensures file exists in R2 and metadata is properly stored
   */
  async verifyUpload(fileId, r2Key) {
    try {
      console.log(`🔍 ENTERPRISE: Verifying upload for file ID: ${fileId}`);
      
      const response = await fetch(`${API_BASE_URL}/api/upload/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileId,
          r2Key
        })
      });

      const result = await handleResponse(response);
      
      if (result.success) {
        console.log(`✅ ENTERPRISE: Upload verified successfully for file ID: ${fileId}`);
        return { success: true, fileId, verified: true };
      } else {
        console.error(`❌ ENTERPRISE: Upload verification failed for file ID: ${fileId}`, result.error);
        return { success: false, error: result.error, fileId };
      }
    } catch (error) {
      console.error(`❌ ENTERPRISE: Upload verification error for file ID ${fileId}:`, error);
      return { success: false, error: error.message, fileId };
    }
  }
};

// Folder API
export const folderAPI = {
  /**
   * Get folder information and files
   */
  async getFolder(folderId) {
    // Use the folder view endpoint; add cache-busting
    const cb = Date.now();
    const response = await fetch(`${API_BASE_URL}/api/folder/${folderId}?cb=${cb}`, { cache: 'no-store' });
    return handleResponse(response);
  },

  /**
   * Get folder files using optimized D1 endpoint (FAST)
   */
  async getFolderFilesOptimized(folderId, page = 1, limit = 500) { // Increased from 100 to 500
    const cb = Date.now();
    const response = await fetch(`${API_BASE_URL}/api/folder-files/${folderId}?page=${page}&limit=${limit}&cb=${cb}`, { cache: 'no-store' });
    return handleResponse(response);
  },

  /**
   * Get fast presigned download URLs for all files in a folder in 1 batch request
   */
  async getDownloadBatch(folderId, fileIds = null) {
    const options = {
      method: fileIds && fileIds.length > 0 ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-store'
    };
    if (fileIds && fileIds.length > 0) {
      options.body = JSON.stringify({ fileIds });
    }
    const response = await fetch(`${API_BASE_URL}/api/download-batch/${folderId}`, options);
    return handleResponse(response);
  },

  /**
   * Delete a folder
   */
  async deleteFolder(folderId) {
    try {
      const userId = getUserId();
      console.log(`🗑️ Deleting folder ${folderId} for user ${userId}`);
      
      const response = await fetch(`${API_BASE_URL}/api/delete-folder/${folderId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userId
        })
      });

      console.log(`🗑️ Delete response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`🗑️ Delete failed with status ${response.status}:`, errorText);
        
        // Parse error response if it's JSON
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || errorData.message || 'Failed to delete folder');
        } catch (parseError) {
          throw new Error(errorText || `Failed to delete folder (${response.status})`);
        }
      }

      const result = await response.json();
      console.log(`🗑️ Delete successful:`, result);
      return result;
      
    } catch (error) {
      console.error('🗑️ Delete folder error:', error);
      
      // Provide more specific error context
      if (error.message.includes('Failed to fetch')) {
        throw new Error('Network error: Unable to reach server. Please check your connection and try again.');
      }
      
      throw error;
    }
  },

  /**
   * Delete a file
   */
  async deleteFile(folderId, fileId) {
    try {
      const userId = getUserId();
      const response = await fetch(`${API_BASE_URL}/api/delete-file/${folderId}/${fileId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to delete file');
      }

      return await response.json();
    } catch (error) {
      console.error('Delete file error:', error);
      throw error;
    }
  },

  /**
   * Create a new folder
   */
  async createFolder(folderName, parentFolderId = null) {
    const userId = getUserId();
    
    const response = await fetch(`${API_BASE_URL}/api/folder/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        folderName,
        parentFolderId,
        userId 
      })
    });

    return handleResponse(response);
  }
};

// Download API
export const downloadAPI = {
  /**
   * Get optimized download URL with CDN support (returns JSON with signed URL)
   */
  async downloadFile(folderId, fileId) {
    const response = await fetch(`${API_BASE_URL}/api/download/${folderId}/${fileId}`);
    if (response.status === 403) {
      try {
        const cloned = response.clone();
        const json = await cloned.json();
        if (json?.code === 'BANDWIDTH_EXCEEDED') {
          window.__bandwidthBlocked = { active: true, details: json };
          window.dispatchEvent(new CustomEvent('bandwidth-exceeded', { detail: json }));
        } else if (json?.code === 'STORAGE_EXCEEDED') {
          window.__storageBlocked = { active: true, details: json };
          window.dispatchEvent(new CustomEvent('storage-quota-exceeded', { detail: json }));
        }
      } catch (_) {}
    }
    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }
    
    // Parse JSON response containing signed download URL
    const downloadData = await response.json();
    
    if (!downloadData.success || !downloadData.downloadUrl) {
      throw new Error(downloadData.error || 'No download URL received');
    }
    
    return downloadData; // Return download data with optimized URL
  },

  /**
   * Track download completion
   */
  async trackDownload(fileId, folderId, bytesDownloaded) {
    const response = await fetch(`${API_BASE_URL}/api/download/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fileId,
        folderId,
        bytesDownloaded
      })
    });

    return handleResponse(response);
  }
};

// User API
export const userAPI = {
  /**
   * Get user's folders (My Files)
   */
  async getUserFolders() {
    const userId = await getUserIdWhenReady();
    const bookmarkKey = userId ? `fileshare_d1_bookmark_${userId}` : null;
    let bookmark = null;
    try { bookmark = bookmarkKey ? localStorage.getItem(bookmarkKey) : null; } catch (_) {}
    
    // Use optimized folders endpoint (D1) to list folders directly
    const response = await fetch(`${API_BASE_URL}/api/folders/paginated`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-store',
        ...(bookmark ? { 'X-D1-Bookmark': bookmark } : {})
      },
      // High limit to ensure all folders are returned; pagination can be added later
      body: JSON.stringify({ userId, page: 1, limit: 1000 })
    });
    const nextBookmark = response.headers.get('X-D1-Bookmark');
    if (nextBookmark && bookmarkKey) {
      try { localStorage.setItem(bookmarkKey, nextBookmark); } catch (_) {}
    }
    const data = await handleResponse(response);
    
    if (data && data.folders) {
      // Normalize to previous shape expected by MyFilesPage
      const folders = (data.folders || []).map(f => ({
        folderId: f.folder_id || f.folderId,
        folder_name: f.folder_name || f.folderName,
        folderName: f.folder_name || f.folderName,
        createdAt: f.created_at || f.createdAt,
        lastAccessedAt: f.lastAccessed || f.lastAccessedAt || f.created_at || f.createdAt,
        totalDownloads: f.total_downloads || f.totalDownloads || 0,
        bandwidthUsed: f.bandwidth_used || f.bandwidthUsed || 0,
        fileCount: f.file_count || f.fileCount || 0,
        totalSize: f.total_size || f.totalSize || 0,
        shareUrl: `${process.env.REACT_APP_FRONTEND_URL || window.location.origin}/d/${f.folder_id || f.folderId}`,
        expiryDate: f.expiry_date || f.expiryDate || null,
        files: []
      }));
      return { success: true, userId, folders, pagination: data.pagination };
    }
    
    // Fallback to older method if needed
    return data;
  },

  /**
   * Get user statistics
   */
  async getUserStats() {
    const userId = getUserId();
    
    const response = await fetch(`${API_BASE_URL}/api/user/${userId}/stats`);
    return handleResponse(response);
  },

  /**
   * Create a new user
   */
  async createUser() {
    const response = await fetch(`${API_BASE_URL}/api/user/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    const data = await handleResponse(response);
    
    if (data.userId) {
      localStorage.setItem('fileshare_user_id', data.userId);
    }
    
    return data;
  },

  async renameFolder(folderId, newFolderName) {
    const userId = getUserId();
    console.log('Renaming folder:', { folderId, newFolderName, userId });
    
    const response = await fetch(`${API_BASE_URL}/api/folder/${folderId}/rename`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        folderName: newFolderName, 
        userId
      })
    });

    return handleResponse(response);
  },

  async renameFile(fileId, newFilename) {
    const userId = getUserId();
    const response = await fetch(`${API_BASE_URL}/api/file/${fileId}/rename`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ newFilename, userId })
    });
    return handleResponse(response);
  },
};

// Request folders API
export const requestAPI = {
  async createRequestFolder(userId, folderName) {
    const response = await fetch(`${API_BASE_URL}/api/request-folders/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, folderName })
    });
    return handleResponse(response);
  },
  async listRequestFolders(userId) {
    const cb = Date.now();
    const response = await fetch(`${API_BASE_URL}/api/request-folders/list?userId=${encodeURIComponent(userId)}&cb=${cb}`, { cache: 'no-store' });
    return handleResponse(response);
  }
};

// Utility functions
export const utils = {
  /**
   * Format file size
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  /**
   * Format date
   */
  formatDate(dateString) {
    if (!dateString) return '';
    let date;
    try {
      if (typeof dateString === 'number') {
        // If seconds, convert to ms
        date = new Date(dateString < 1e12 ? dateString * 1000 : dateString);
      } else if (typeof dateString === 'string') {
        const s = dateString.trim();
        if (/^\d+$/.test(s)) {
          const ms = s.length === 10 ? parseInt(s, 10) * 1000 : parseInt(s, 10);
          date = new Date(ms);
        } else {
          // If no timezone info, assume UTC and append Z
          const hasTZ = /[zZ]|[+-]\d\d:?\d\d$/.test(s);
          date = new Date(hasTZ ? s : `${s}Z`);
        }
      } else {
        date = new Date(dateString);
      }
    } catch {
      date = new Date(dateString);
    }

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const opts = { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: tz };
    try {
      return new Intl.DateTimeFormat(undefined, opts).format(date);
    } catch {
      return date.toLocaleString();
    }
  },

  /**
   * Generate share URL
   */
  generateShareUrl(folderId) {
    const frontendUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
    return `${frontendUrl}/d/${folderId}`;
  },

  /**
   * Debug: Get current user ID
   */
  getCurrentUserId() {
    return getUserId();
  },

  getCurrentUserIdWhenReady() {
    return getUserIdWhenReady();
  },

  /**
   * Debug: Clear user ID (will generate new one on next action)
   */
  clearUserId() {
    localStorage.removeItem('fileshare_user_id');
    console.log('User ID cleared. New ID will be generated on next action.');
  },

  /**
   * Debug: Set specific user ID (for testing)
   */
  setUserId(userId) {
    localStorage.setItem('fileshare_user_id', userId);
    console.log('User ID set to:', userId);
  },

  /**
   * Test upload speed
   */
  async testUploadSpeed() {
    const startTime = Date.now();
    console.log('🚀 Starting upload speed test...');
    
    try {
      const response = await fetch(`${API_BASE_URL}/health`);
      const endTime = Date.now();
      const latency = endTime - startTime;
      
      console.log(`📡 Backend latency: ${latency}ms`);
      
      if (latency > 1000) {
        console.warn('⚠️ High latency detected. Upload speeds may be affected.');
        console.log('💡 Tips: Check your internet connection and R2 endpoint region.');
      } else {
        console.log('✅ Low latency - good for fast uploads!');
      }
      
      return { latency, status: 'healthy' };
    } catch (error) {
      console.error('❌ Speed test failed:', error);
      return { latency: 999999, status: 'error', error: error.message };
    }
  }
};

// DEPRECATED: Upload single file to backend with timeout handling for large files
// ⚠️ WARNING: This function is deprecated and should not be used for new code.
// Use uploadAPI.uploadToR2Smart() instead for better large file support.
export async function uploadFile(file, userId) {
  console.warn('⚠️ DEPRECATED: uploadFile() is deprecated. Use uploadAPI.uploadToR2Smart() instead.');
  console.log(`Starting upload for file: ${file.name}, size: ${file.size} bytes, type: ${file.type}`);
  
  // Check if file exceeds 100MB limit - use chunked upload
  const CHUNK_SIZE = 90 * 1024 * 1024; // 90MB chunks (leave buffer under 100MB limit)
  if (file.size > CHUNK_SIZE) {
    console.log('File exceeds 90MB, using chunked upload');
    return uploadFileChunked(file, userId);
  }
  
  const formData = new FormData();
  formData.append('file', file);
  formData.append('userId', userId);

  // Create AbortController for timeout handling
  const controller = new AbortController();
  
  // Set timeout based on file size (minimum 5 minutes, add 1 minute per 50MB)
  const timeoutMs = Math.max(300000, Math.ceil(file.size / (50 * 1024 * 1024)) * 60000);
  console.log(`Upload timeout set to: ${timeoutMs / 1000} seconds`);
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    console.log(`Uploading to: ${API_BASE_URL}/api/upload`);
    const response = await fetch(`${API_BASE_URL}/api/upload`, {
      method: 'POST',
      body: formData,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    console.log(`Upload response status: ${response.status}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Upload failed with error:', errorData);
      throw new Error(errorData.message || `Upload failed with status ${response.status}`);
    }

    const result = await response.json();
    console.log('Upload successful:', result);
    return result;
  } catch (error) {
    clearTimeout(timeoutId);
    console.error('Upload error caught:', error);
    if (error.name === 'AbortError') {
      throw new Error(`Upload timed out after ${timeoutMs / 1000} seconds. Please try again or check your connection.`);
    }
    if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
      throw new Error('Network error: Please check your internet connection and try again.');
    }
    throw error;
  }
}

// DEPRECATED: Chunked upload function for large files (>90MB)
// ⚠️ WARNING: This function is deprecated and should not be used for new code.
// Use uploadAPI.uploadToR2Smart() instead for better large file support.
async function uploadFileChunked(file, userId) {
  console.warn('⚠️ DEPRECATED: uploadFileChunked() is deprecated. Use uploadAPI.uploadToR2Smart() instead.');
  const CHUNK_SIZE = 90 * 1024 * 1024; // 90MB chunks
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  console.log(`Uploading ${file.name} in ${totalChunks} chunks of ${CHUNK_SIZE / (1024 * 1024)}MB each`);
  
  // Generate unique upload session ID
  const uploadId = 'upload-' + Math.random().toString(36).substr(2, 16);
  console.log(`Upload session ID: ${uploadId}`);
  
  try {
    // Upload each chunk
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      
      console.log(`Uploading chunk ${chunkIndex + 1}/${totalChunks} (${start}-${end} bytes)`);
      
      const formData = new FormData();
      formData.append('chunk', chunk);
      formData.append('userId', userId);
      formData.append('uploadId', uploadId);
      formData.append('chunkIndex', chunkIndex.toString());
      formData.append('totalChunks', totalChunks.toString());
      formData.append('originalFilename', file.name);
      formData.append('originalFileSize', file.size.toString());
      formData.append('fileType', file.type);
      
      const response = await fetch(`${API_BASE_URL}/api/upload-chunk`, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Chunk ${chunkIndex + 1} upload failed: ${errorData.message || response.statusText}`);
      }
      
      const result = await response.json();
      console.log(`Chunk ${chunkIndex + 1} uploaded successfully`);
      
      // If this is the last chunk, return the final result
      if (chunkIndex === totalChunks - 1) {
        console.log('All chunks uploaded successfully, file assembled');
        return result;
      }
    }
  } catch (error) {
    console.error('Chunked upload failed:', error);
    // Try to cleanup failed upload
    try {
      await fetch(`${API_BASE_URL}/api/cleanup-upload/${uploadId}`, { method: 'DELETE' });
    } catch (cleanupError) {
      console.warn('Failed to cleanup upload:', cleanupError);
    }
    throw error;
  }
}

// Upload multiple files to backend (creates a single folder with all files)
export async function uploadMultipleFiles(files, userId, onFileProgress) {
  const formData = new FormData();
  
  // Add all files to the form data
  files.forEach((file, index) => {
    formData.append('files', file);
    if (onFileProgress) {
      onFileProgress(index);
    }
  });
  
  formData.append('userId', userId);

  // Create AbortController for timeout handling
  const controller = new AbortController();
  
  // Calculate total size and set timeout accordingly
  const totalSize = files.reduce((sum, file) => sum + file.size, 0);
  const timeoutMs = Math.max(600000, Math.ceil(totalSize / (50 * 1024 * 1024)) * 60000);
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${API_BASE_URL}/api/upload-multiple`, {
      method: 'POST',
      body: formData,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Upload failed');
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Upload timed out. Please try again or check your connection.');
    }
    throw error;
  }
}

// Get folder contents
export async function getFolderContents(folderId) {
  return apiRequest(`/api/folder/${folderId}`);
}

// Health check
export async function healthCheck() {
  return apiRequest('/api/health');
}

// Export formatFileSize directly for convenience
export const formatFileSize = utils.formatFileSize;

// Lightweight upload metrics recorder (in-memory)
const uploadMetrics = {
  samples: [],
  retries: 0,
  record(durationMs, { method, size }) {
    this.samples.push({ t: Date.now(), d: durationMs, m: method, s: size });
    if (this.samples.length > 2000) this.samples.shift();
  },
  recordRetry() { this.retries += 1; },
  summary() {
    if (this.samples.length === 0) return { count: 0, p95: 0, p99: 0, retries: this.retries };
    const arr = this.samples.map(x => x.d).sort((a,b)=>a-b);
    const pct = (p) => arr[Math.min(arr.length - 1, Math.floor((p/100) * arr.length))];
    return { count: arr.length, p95: pct(95), p99: pct(99), retries: this.retries };
  }
};
